/**
 * Date-of-birth selectable bounds, by patient type — Flutter parity.
 *
 * Mirrors the Flutter DOB date-picker constraints exactly:
 *   - Patient DOB (`personal_details_form.dart:443-450`):
 *       Adult ⇒ firstDate = null,  lastDate = now − (365×18 + 1) days
 *       Minor ⇒ firstDate = now − 365×18 days,  lastDate = now
 *   - Guardian DOB (`guardians_identity_verification.dart:232`):
 *       lastDate = now − (365×18 + 1) days (a guardian must be an adult)
 *
 * Flutter uses a **day-based** `Duration(days: 365 * 18)` rather than exact
 * calendar years; we replicate that day math verbatim so the Adult/Minor
 * cutoff behaves identically across both apps (don't "improve" it to
 * calendar-accurate 18y — that would diverge ~5 days at the boundary).
 */

/** Flutter `Duration(days: 365 * 18)`. */
const DOB_AGE_CUTOFF_DAYS = 365 * 18;

function subDays(from: Date, days: number): Date {
  const d = new Date(from);
  d.setDate(d.getDate() - days);
  return d;
}

/** Latest DOB allowed for an adult: strictly older than 18 (today − 18y − 1d). */
export function adultMaxDob(today: Date = new Date()): Date {
  return subDays(today, DOB_AGE_CUTOFF_DAYS + 1);
}

/** Earliest DOB allowed for a minor: within the last 18 years (today − 18y). */
export function minorMinDob(today: Date = new Date()): Date {
  return subDays(today, DOB_AGE_CUTOFF_DAYS);
}
